import * as actionTypes from "../actionTypes";
import * as binderTabsActions from "./binderTabsActions";
import * as binderWallColumns from "../enums/binderWallColumns";
import * as cacheManager from "../lib/cacheManager";
import * as qs from "../lib/queryString";
import * as queryStringFields from "../enums/queryStringFields";
import * as routeHelpers from "../lib/routeHelpers";
import * as targetLayout from "../lib/searchResults/targetLayout";
import * as userContentKeys from "../enums/userContentKeys";
import * as users from "../lib/api/users";
import find from "lodash.find";
import getBinderWallColumnToggled from "../lib/getBinderWallColumnToggled";
import getDefaultColumnsForTarget from "../lib/getDefaultColumnsForTarget";
import getFiltersOpen, { DEFAULT_FILTERS_VALUE } from "../lib/getFiltersOpen";
import handleUnknownError from "../lib/handleUnknownError";
import history from "../lib/history";
import initialContentValue from "../lib/initialContentValue";
import safeDecodeURIComponent from "../lib/safeDecodeURIComponent";
import _refiner from "refiner-js";

const generateSignature = async (userId, secretKey) => {
	if (typeof crypto?.subtle !== "undefined") {
		// We're in the browser and can safely use the Web Crypto API
		const encoder = new TextEncoder();
		const keyData = encoder.encode(secretKey);
		const data = encoder.encode(userId);

		// Import the key
		const key = await crypto.subtle.importKey(
			"raw",
			keyData,
			{ name: "HMAC", hash: { name: "SHA-256" } },
			false,
			["sign"],
		);

		// Generate HMAC signature
		const signature = await crypto.subtle.sign("HMAC", key, data);

		// Convert ArrayBuffer to hex string
		return Array.from(new Uint8Array(signature))
			.map((byte) => byte.toString(16).padStart(2, "0"))
			.join("");
	} else {
		// Fallback: Handle the case where `crypto.subtle` is not available (e.g., on the server)
		console.error("Web Crypto API is not available.");
	}
};

const BINDER_WALL_COLS = Object.keys(binderWallColumns).map((k) => binderWallColumns[k]); // eslint-disable-line import/namespace
const INVALID_BINDER = "INVALID_BINDER";
const INVALID_BINDER_TAB = "INVALID_BINDER_TAB";
const INVALID_TARGET = "INVALID_TARGET";
const UNAUTHORIZED_OPERATION = "UNAUTHORIZED_OPERATION";

let Sentry;

if (__ERROR_REPORTING__) {
	import("@sentry/react").then((module) => {
		Sentry = module.default;
	});
}

const setAuthenticatedImmediate = (authenticated, currentUser, suspendPageAuth) => ({
	type: actionTypes.SESSION_SET_AUTHENTICATED,
	authenticated,
	currentUser,
	suspendPageAuth,
});

const setPageAuthSuspended = (suspendPageAuth) => ({
	type: actionTypes.SESSION_SET_PAGE_AUTH_SUSPENDED,
	suspendPageAuth,
});

export const setAuthenticated = (authenticated, user) => {
	return (dispatch, getState) => {
		if (Sentry) {
			if (authenticated) {
				Sentry.setUser({
					email: user.email,
					id: user.id,
				});
			} else {
				Sentry.setUser();
			}
		}

		if (authenticated) {
			getState()
				.page.siPromise.then((si) => {
					const name = `${user.first_name} ${user.last_name}`;
					si.setChatInfo(name, user.email);
				})
				.catch((e) => handleUnknownError(e, false));

			setTimeout(() => {
				const heap = window.heap;
				heap && heap.identify(user.id);

				const userId = String(user.id);
				const signature = generateSignature(userId, __REFINER_SECRET_KEY__);
				_refiner("setProject", __REFINER_PROJECT_ID__, {
					signature: signature,
				});
				_refiner(
					"identifyUser",
					{
						id: userId,
						email: user.email,
						name: `${!!user.first_name ? user.first_name : ""} ${
							!!user.last_name ? user.last_name : ""
						}`,
					},
					{
						signature: signature,
					},
				);
			}, 500);

			return Promise.resolve(dispatch(setAuthenticatedImmediate(authenticated, user)));
		}

		// If logging out, clear cache
		return cacheManager.clearAll().then(() => {
			return dispatch(setAuthenticatedImmediate(authenticated, user));
		});
	};
};

export const logoutAndRedirect = (useDestinationQuery) => {
	return (dispatch) => {
		if (Sentry) {
			Sentry.setUser();
		}

		return cacheManager.clearAll().then(() => {
			dispatch(setPageAuthSuspended(true));

			const query = useDestinationQuery ? window.location.search : null;
			const pathname = useDestinationQuery ? window.location.pathname : null;
			const nextPath = routeHelpers.getLoginRedirect(pathname, query);

			history.push(nextPath);

			return dispatch(setAuthenticatedImmediate(false, undefined, false));
		});
	};
};

export const loginAndRedirect = (user, redirectURL) => {
	return (dispatch, getState) => {
		if (Sentry) {
			Sentry.setUser({
				email: user.email,
				id: user.id,
			});
		}

		getState()
			.page.siPromise.then((si) => {
				const name = `${user.first_name} ${user.last_name}`;
				si.setChatInfo(name, user.email);
			})
			.catch((e) => handleUnknownError(e, false));

		dispatch(setPageAuthSuspended(true));
		dispatch(setAuthenticatedImmediate(true, user));

		if (redirectURL) {
			window.location.href = redirectURL;
			return;
		}

		const queryParams = qs.parse(window.location.search);
		const { destination, url, ...remainingParams } = queryParams;

		const decodedUrl = safeDecodeURIComponent(url, "");
		if (decodedUrl) {
			window.location.href = decodedUrl;
			dispatch(setPageAuthSuspended(false));
			return;
		}

		const isIntroEnabled = !user.content || !user.content[userContentKeys.DISABLE_INTRO];
		if (isIntroEnabled) {
			remainingParams[queryStringFields.INTRO] = true;
		}

		const decodedDestination = safeDecodeURIComponent(destination, "");
		let finalRedirectURL = `/?${qs.stringify(remainingParams)}`;
		if (decodedDestination) {
			finalRedirectURL =
				remainingParams && Object.keys(remainingParams).length
					? `${decodedDestination}?${qs.stringify(remainingParams)}`
					: decodedDestination;
		}

		window.location.href = finalRedirectURL;
		dispatch(setPageAuthSuspended(false));
	};
};

export const updateCurrentUser = (user) => ({
	type: actionTypes.SESSION_UPDATE_USER,
	user,
});

export const updateCurrentUserPracticeAreas = (practiceAreas) => ({
	type: actionTypes.SESSION_UPDATE_USER_PRACTICE_AREAS,
	practiceAreas,
});

export const updateCurrentUserToken = (token) => ({
	type: actionTypes.SESSION_UPDATE_USER_TOKEN,
	token,
});

export const toggleDefaultFiltersOpen = () => {
	return (dispatch, getState) => {
		const state = getState();
		const user = state.session.currentUser;

		if (!user) {
			return Promise.resolve();
		}

		const content = { ...user.content };

		const initialValue = getFiltersOpen(user);

		const nextValue = !initialValue;
		if (nextValue === DEFAULT_FILTERS_VALUE) {
			delete content[userContentKeys.FILTERS_OPEN_DEFAULT];
		} else {
			content[userContentKeys.FILTERS_OPEN_DEFAULT] = nextValue;
		}

		const updatedSessionUser = {
			...state.session.currentUser,
			content: content,
		};

		dispatch(updateCurrentUser(updatedSessionUser));

		return users
			.update(user.id, null, null, null, null, null, null, null, content)
			.catch((err) => {
				dispatch(updateCurrentUser(user));
				return Promise.reject(err);
			});
	};
};

export const toggleDisableIntro = () => {
	return (dispatch, getState) => {
		const state = getState();
		const user = state.session.currentUser;

		if (!user) {
			return Promise.resolve();
		}

		const disableIntro = initialContentValue(user, userContentKeys.DISABLE_INTRO, false);

		const content = {
			...user.content,
			[userContentKeys.DISABLE_INTRO]: !disableIntro,
		};
		const updatedSessionUser = {
			...state.session.currentUser,
			content: content,
		};

		dispatch(updateCurrentUser(updatedSessionUser));

		return users
			.update(user.id, null, null, null, null, null, null, null, content)
			.catch((err) => {
				dispatch(updateCurrentUser(user));
				return Promise.reject(err);
			});
	};
};

export const updateUserTargetLayout = (targetName, fields, order, widths) => {
	return (dispatch, getState) => {
		const state = getState();
		const user = state.session.currentUser;
		const target = state.targets[targetName];
		const binder = state.binders[state.ui.binderId];
		const binderTab = state.binderTabs[state.ui.binderTabId];

		if (!user) {
			return Promise.reject(new Error(UNAUTHORIZED_OPERATION));
		}
		if (!targetName || !target) {
			return Promise.reject(new Error(INVALID_TARGET));
		}
		if (!binderTab || !binderTab.search) {
			return Promise.reject(new Error(INVALID_BINDER_TAB));
		}
		if (!binder) {
			return Promise.reject(new Error(INVALID_BINDER));
		}

		const content =
			fields && order && widths
				? targetLayout.updateLayouts(
						user.content,
						targetName,
						binder.type,
						fields,
						widths,
						order,
					)
				: targetLayout.resetLayout(user.content, targetName, binder.type);

		return users
			.update(user.id, null, null, null, null, null, null, null, content)
			.then((updatedUser) => {
				dispatch(updateCurrentUser(updatedUser));
				const targetFields = target.fieldIds.map((id) => state.fields[id]);
				const tabFields = fields
					? fields
					: getDefaultColumnsForTarget(
							targetFields,
							binderTab.search.criteria,
							state.criterionEditors,
						);
				dispatch(
					binderTabsActions.setBinderTabsSearchFieldNames(
						binderTab.binderId,
						binderTab.id,
						tabFields,
					),
				);
				dispatch(
					binderTabsActions.setBinderTabsSearchColOrder(
						binderTab.binderId,
						binderTab.id,
						null,
					),
				);
				dispatch(
					binderTabsActions.setBinderTabsSearchColWidth(
						binderTab.binderId,
						binderTab.id,
						null,
					),
				);
				return updatedUser;
			});
	};
};

export const setDefaultStartTarget = (targetName, libraryName) => {
	return (dispatch, getState) => {
		const state = getState();
		const user = state.session.currentUser;

		if (!user) {
			return Promise.resolve();
		}

		const content = {
			...user.content,
		};

		const targetNameNoLib =
			targetName && targetName.indexOf(libraryName) === 0
				? targetName.slice(libraryName.length)
				: targetName;
		if (targetNameNoLib === content[userContentKeys.DEFAULT_START_TARGET]) {
			return state;
		}

		if (targetNameNoLib) {
			content[userContentKeys.DEFAULT_START_TARGET] = targetNameNoLib;
		} else {
			delete content[userContentKeys.DEFAULT_START_TARGET];
		}

		const updatedSessionUser = {
			...state.session.currentUser,
			content: content,
		};

		dispatch(updateCurrentUser(updatedSessionUser));

		return users
			.update(user.id, null, null, null, null, null, null, null, content)
			.catch((err) => {
				dispatch(updateCurrentUser(user));
				return Promise.reject(err);
			});
	};
};

export const setDefaultQuickProfileLanding = (
	quickProfileLabel,
	tabTitle,
	noneSelected = false,
	reset = false,
) => {
	return (dispatch, getState) => {
		const state = getState();
		const user = state.session.currentUser;
		if (!user) {
			return Promise.resolve();
		}

		const content = { ...user.content };
		const newQuickProfileLandings = {
			...content[userContentKeys.DEFAULT_PROFILE_LANDING_TABS],
		};
		if (reset === true) {
			delete content[userContentKeys.DEFAULT_PROFILE_LANDING_TABS];
		} else {
			if (!noneSelected) {
				newQuickProfileLandings[quickProfileLabel] = tabTitle;
			} else {
				delete newQuickProfileLandings[quickProfileLabel];
			}
			content[userContentKeys.DEFAULT_PROFILE_LANDING_TABS] = newQuickProfileLandings;
		}

		const updatedSessionUser = {
			...state.session.currentUser,
			content,
		};

		dispatch(updateCurrentUser(updatedSessionUser));
		return users
			.update(user.id, null, null, null, null, null, null, null, content)
			.catch((err) => {
				dispatch(updateCurrentUser(user));
				return Promise.reject(err);
			});
	};
};

export function reviewLitigationHistory() {
	return (dispatch, getState) => {
		const state = getState();

		const user = state.session.currentUser;
		if (!user) {
			return Promise.resolve();
		}

		const reviewDate = new Date(Date.now());
		const updatedSessionUser = {
			...user,
			last_lit_history: reviewDate,
		};

		dispatch(updateCurrentUser(updatedSessionUser));
		return users
			.update(user.id, null, null, null, null, null, null, null, null, null, !!reviewDate)
			.catch((err) => {
				dispatch(updateCurrentUser(user));
				return Promise.reject(err);
			});
	};
}

export function toggleBinderWallColumn(columnName) {
	return (dispatch, getState) => {
		const state = getState();

		const user = state.session.currentUser;
		if (!user) {
			return Promise.resolve();
		}

		const columnInfo = find(BINDER_WALL_COLS, (col) => col.name === columnName);
		if (!columnInfo) {
			return Promise.resolve();
		}

		const userColumns = initialContentValue(user, userContentKeys.BINDER_WALL_COLUMNS, {});
		const nextUserColumns = {
			...userColumns,
		};

		const defaultToggled = !!columnInfo.default;
		const nextToggled = !getBinderWallColumnToggled(userColumns, columnName);

		if (defaultToggled === nextToggled) {
			delete nextUserColumns[columnName];
		} else {
			nextUserColumns[columnName] = nextToggled;
		}

		const nextContent = {
			...user.content,
			[userContentKeys.BINDER_WALL_COLUMNS]: nextUserColumns,
		};

		const updatedUser = {
			...user,
			content: nextContent,
		};

		dispatch(updateCurrentUser(updatedUser));

		return users
			.update(user.id, null, null, null, null, null, null, null, nextContent)
			.catch((err) => {
				dispatch(updateCurrentUser(user));
				return Promise.reject(err);
			});
	};
}

export const sharePatentCloud = () => {
	return (dispatch, getState) => {
		const state = getState();
		const user = state.session.currentUser;

		if (!user) {
			return Promise.resolve();
		}

		return users.sharePatentCloud().then((sauce) => {
			const updatedSessionUser = {
				...state.session.currentUser,
				sauce,
			};

			dispatch(updateCurrentUser(updatedSessionUser));
		});
	};
};

export const updateCurrentUserContent = (content) => {
	return (dispatch, getState) => {
		const state = getState();
		const user = state.session.currentUser;
		if (!user) {
			return Promise.resolve();
		}

		const updatedContent = { ...user.content, ...content };

		return users
			.update(user.id, null, null, null, null, null, null, null, updatedContent)
			.then((updatedUser) => {
				dispatch(updateCurrentUser(updatedUser));
			})
			.catch((err) => {
				dispatch(updateCurrentUser(user));
				return Promise.reject(err);
			});
	};
};
